<template>
  <custom-scrollbar ref="scroll" class="chat-container row" :auto-hide="false">
    <div class="bubble-container offset-md-1 col-md-10 col-12" v-show="!this.isLoading">
      <template v-for="message in this.messages">
        <chat-extra-messages
          v-if="message.entity === 'bot' && message.extraMessage"
          :message-id="message.id"
          :extra-message="message.extraMessage"
          :total-messages="this.messages.length"
          @newsletter-clicked="newsletterClicked"/>

        <chat-bubble
          :message="message"
          :total-messages="this.messages.length"
          @update-messages="updateMessages"
          @login-opened="loginOpened"
          @send-feedback="sendFeedbackToAPI"
          @set-error="setErrorMessage"/>

        <chat-products
          v-if="message.products && message.products.length > 0"
          :message-id="message.id"
          :total-messages="this.messages.length"
          :products="message.products"
          @likeProduct="likeProduct"
          @clickedProduct="clickedProduct"/>

        <chat-chips
          v-if="message.entity === 'bot' && message.chips"
          :chips="message.chips"
          :message-id="message.id"
          :total-messages="this.messages.length"
          @selectChip="sendChip"/>
      </template>

      <div class="expired-session" v-if="this.chatExpired" v-show="!this.isLoading">
        <template v-if="this.chatExpired && this.fromHistoryFeedback">
          <div class="expire-from-history">{{ this.$translations.label.expire.sessionexpired }}</div>
        </template>
        <template v-else>
          <div class="thanks">{{ this.$translations.label.expire.thanks.text }}</div>
          <div class="subtext">{{ this.$translations.label.expire.thanks.subtext }}</div>
        </template>
      </div>
    </div>
    <div class="loader-container" v-if="this.isLoading">
      <div class="loader mm-spinner -lg -ecru-95"></div>
    </div>
  </custom-scrollbar>

  <div class="chat-action" v-show="!this.isLoading">
    <chat-form-input
      :disableTextInput="this.disableTextInput"
      :total-messages="this.messages.length"
      :chat-expired="this.chatExpired"
      @send="sendMessage"
      @restartChat="restartChat" />
  </div>

  <error-bubble
    v-if="this.showError"
    @close-error="hideErrorMessage"
    :error-message="this.errorMessage"
  />
</template>

<script>
import CustomScrollbar from 'custom-vue-scrollbar';
import ChatFormInput from './chat-form-input.vue';
import ChatBubble from './chat-bubble.vue';
import ChatProducts from './chat-products.vue';
import ChatChips from './chat-chips.vue';
import ChatExtraMessages from './chat-extra-messages.vue';
import ErrorBubble from '../error/error-bubble.vue';

export default {
    data() {
      return {
        messages: [],
        disableTextInput: false,
        isLoading: true,
        user: {},
        idleTimer: null,
        carouselNotFromHistory: false,
        resetTimer: true,
        feedbackMessageSended: false,
        chatExpired: false,
        fromHistoryFeedback: false,
        showError: false,
        errorMessage: '',
        defaultSuggestionChips: []
      }
    },
    components: {
      CustomScrollbar,
      ChatFormInput,
      ChatBubble,
      ChatProducts,
      ChatChips,
      ChatExtraMessages,
      ErrorBubble
    },
    methods: {
      initChat() {
        this.$chatbotAppElement.trigger('chatbot:init', {chatThreadId: this.chatThreadId});
        let firstMessage = {
          id: 0,
          entity: 'bot',
          delivered: true,
        }

        if (this.user && Object.keys(this.user.profile).length > 0) {
          firstMessage.text = this.$translations.messages.intromessages.logged.message.replace('§',this.user.profile.firstName);
          firstMessage.subtext = this.$translations.messages.intromessages.logged.sub;
        } else {
          this.setDefaultFirstMessage(firstMessage);
        }

        this.messages.push(firstMessage);
        this.checkLastMessages();
      },
      setDefaultFirstMessage(firstMessage) {
        firstMessage.userNotLoggedIn = true;
        let guestMessagesArray = [];
        for (let guestMessageIndex in this.$translations.messages.intromessages.guest) {
          guestMessagesArray.push(this.$translations.messages.intromessages.guest[guestMessageIndex]);
        }

        const shuffled = [...guestMessagesArray].sort(() => 0.5 - Math.random());
        let shuffledGuestMessage = shuffled.slice(0, 1)[0];

        firstMessage.text = shuffledGuestMessage.message;
        firstMessage.subtext = shuffledGuestMessage.sub;
        firstMessage.chips = this.defaultSuggestionChips;
      },
      updateMessages(loggedCustomer) {
        if (loggedCustomer) {
          let firstMessage = this.messages[0];
          firstMessage.text = this.$translations.messages.intromessages.logged.message.replace('§', loggedCustomer.profile.firstName);
          firstMessage.subtext = this.$translations.messages.intromessages.logged.sub;
          firstMessage.userNotLoggedIn = false;
          this.updateChatId(loggedCustomer);
        }
      },
      sendChip(chipText) {
        this.sendMessage(chipText);
      },
      async likeProduct(likedProduct) {
        this.disableTextInput = true;
        let compositeMessage = this.$translations.messages.likedproduct.replace('§', likedProduct.name);
        let apiResponse;

        try {
          apiResponse = await this.$http
            .post(this.$urls.chatLikeProductUrl, {
              userId: this.user.userId,
              productId: likedProduct.id
            })
        } catch (error) {
            console.error(error);
            this.setErrorMessage(this.$translations.error.generic);
        } finally {
          this.sendMessage(compositeMessage, likedProduct);
        }
      },
      async sendMessage(message, productObj, resendMessage) {
        this.disableTextInput = true;
        this.$chatbotAppElement.trigger('chatbot:messageSent', {chatThreadId: this.chatThreadId});
        if (!resendMessage) {
          this.messages.push(
            {
              id: this.messages.length + 1,
              text: message,
              entity: 'sender',
              delivered: true
            }
          )
        }

        let apiResponse;

        try {
          this.messages.push(
            {
              id: this.messages.length + 1,
              text: '',
              entity: 'bot',
              delivered: true
            }
          )
          this.scrollToMessage();

          let body = {
            message: message,
            chatId: this.chatThreadId
          }

          if (productObj) {
            body.likedProduct = productObj.raw;
            body.likeFlag = true;
          }

          apiResponse = await this.$http
            .post(this.$urls.sendMessageUrl, body)
        } catch (error) {
            console.error(error);
            this.setErrorMessage(this.$translations.error.generic);
        } finally {
          if (apiResponse && apiResponse.data) {
            this.messages[this.messages.length-1].text = apiResponse.data.response;

            if (apiResponse.data.products) {
              this.messages[this.messages.length-1].products = apiResponse.data.products;

              if (!this.carouselNotFromHistory) {
                this.carouselNotFromHistory = true;
              }
            }

            if (apiResponse.data.possibleQuestions) {
              this.messages[this.messages.length-1].chips = apiResponse.data.possibleQuestions;
            }

            if (apiResponse.data.supportLinks) {
              this.messages[this.messages.length-1].supportLinks = apiResponse.data.supportLinks;
            }
            this.disableTextInput = false;

            if (apiResponse.data.conversationEnd) {
              this.sendFeedbackMessage();
            } else {
              if (this.messages.length > 2 && !this.feedbackMessageSended && this.carouselNotFromHistory) {
                if (this.idleTimer) {
                  this.resetInactivityTimer();
                } else {
                  this.startInactivityTimer();
                }
              }
            }

            this.scrollToMessage();
          }
        }
      },
      async checkLastMessages() {
        let apiResponse;
        try {
          apiResponse = await this.$http
            .get(this.$urls.chatCheckMessagesUrl + '?chatId=' + this.chatThreadId);
        } catch (error) {
          console.error(error);
          this.setErrorMessage(this.$translations.error.generic);
        } finally {
          this.retrieveChatHistory(apiResponse.data.found);
        }
      },
      async retrieveChatHistory(needToSendLastMessage) {
        let apiResponse;
        try {
          apiResponse = await this.$http
            .get(this.$urls.chatHistoryUrl + '?chatId=' + this.chatThreadId);
        } catch (error) {
          console.error(error);
          this.setErrorMessage(this.$translations.error.generic);
        } finally {
          const history = apiResponse.data.history;
          if (history && history.length > 0) {
            history.forEach((message, i) => {
              let messageObj = {
                delivered: true,
                id: this.messages.length + 1
              };

              if (message.userMessage) {
                // User messages
                messageObj.text = message.userMessage;
                messageObj.entity = 'sender';
              } else if (message.botResponse) {
                // Bot messagges
                messageObj.text = message.botResponse.response;
                messageObj.entity = 'bot';

                if (message.botResponse.products && message.botResponse.products.length > 0) {
                  messageObj.products = message.botResponse.products;

                  messageObj.products.forEach(product => {
                    product.fromHistory = true;
                  })
                }

                if (message.botResponse.possibleQuestions && message.botResponse.possibleQuestions.length > 0) {
                  messageObj.chips = message.botResponse.possibleQuestions;
                }

                if (message.botResponse.supportLinks && message.botResponse.supportLinks.length > 0) {
                  messageObj.supportLinks = message.botResponse.supportLinks;
                }
              } else if (message.userFeedback !== null && message.userFeedback !== undefined) {
                messageObj.text = this.$translations.messages.feedback;
                messageObj.feedback = true;
                messageObj.entity = 'bot';
                messageObj.feedbackValue = message.userFeedback;

                this.chatExpired = true;
                this.fromHistoryFeedback = true;
              }

              this.messages.push(messageObj);
            });

            if (needToSendLastMessage && history[history.length-1].userMessage) {
              this.sendMessage(history[history.length-1].userMessage , null, true);
            }
          } else if (apiResponse.data.suggestionChips) {
            this.defaultSuggestionChips = apiResponse.data.suggestionChips;
            this.messages[0].chips = this.defaultSuggestionChips;
          }

          this.isLoading = false;
          this.scrollToMessage();

          // ONLY FOR DEBUG MODE
          const debugInfo = document.getElementById('chat-info-debug');
          if (debugInfo) {
            debugInfo.querySelector('.chat-id-debug span').innerHTML = this.chatThreadId;
            debugInfo.querySelector('.chat-userid-debug span').innerHTML = this.user.userId;
          }
        }
      },
      scrollToMessage() {
        if (this.$refs.scroll.scrollEl) {
          setTimeout(() => {
            this.$refs.scroll.scrollEl.scrollTo({ top: this.$refs.scroll.scrollEl.scrollHeight, behavior: 'smooth' });

            // move focus to last message
            const bubbleContainerElement = document.querySelector('.bubble-container');
            const lastMessageElement = bubbleContainerElement.querySelector('.chat-bubble:last-child');
            if (lastMessageElement) {
              lastMessageElement.focus();
            }
          }, 500);
        }
      },
      getCookie(cName) {
        const name = cName + "=";
        const cDecoded = decodeURIComponent(document.cookie);
        const cArr = cDecoded .split('; ');
        let res;
        cArr.forEach(val => {
            if (val.indexOf(name) === 0) res = val.substring(name.length);
        })
        return res;
      },
      setCookie(cName, cValue, expireTimestamp) {
        const expires = "expires=" + new Date(expireTimestamp).toUTCString();
        document.cookie = cName + "=" + cValue + "; " + expires + "; path=/";
      },
      createChatThreadId() {
        this.$http
          .get(this.$urls.chatThreadIdUrl)
          .then(response => {
            if (response.data) {
              const chatId = this.chatThreadId = response.data.chatId;
              const expire = response.data.expire;

              if (response.data.suggestionChips) {
                this.defaultSuggestionChips = [];
                response.data.suggestionChips.forEach(defaultChip => {
                  this.defaultSuggestionChips.push(defaultChip);
                });
              }

              if (this.user.profile && Object.keys(this.user.profile).length > 0) {
                this.setCookie('chatThreadIdLogged', chatId, expire);
                this.setCookie('chatThreadLoggedUserId', this.user.userId, expire);
              } else {
                this.setCookie('chatThreadId', chatId, expire);
                this.setCookie('chatThreadUserId', this.user.userId, expire);
              }

              this.initChat();
            }
          });
      },
      updateChatId(newUser){
        const newUserId = newUser.id;
        const newUserProfile = newUser.profile;
        let body = {
          newUserId: newUserId,
          oldUserId: this.user.userId,
          chatId: this.chatThreadId
        }

        this.$http
          .post(this.$urls.updateChatIdUrl, body)
          .then(response => {
            if (response && response.data && response.data.status === 'ok') {
              this.user.userId = newUserId;
              this.user.profile = newUserProfile;
              //update cookie with new chat user id form response - same expire time
              this.setCookie('chatThreadIdLogged', this.chatThreadId, response.data.expireTime);
              this.setCookie('chatThreadLoggedUserId', this.user.userId, response.data.expireTime);
              this.setCookie('chatThreadUserId', this.user.userId, response.data.expireTime);

              this.$chatbotAppElement.trigger('chatbot:loginSubmitted', {chatThreadId: this.chatThreadId, chatUserProfile: this.user.profile});
            }
          });
      },
      restartChat() {
        this.$chatbotAppElement.trigger('chatbot:stop', {chatThreadId: this.chatThreadId});
        this.isLoading = true;
        this.chatExpired = false;
        this.fromHistoryFeedback = false;
        this.messages = [];
        this.createChatThreadId();
      },
      setExtraMessage(message) {
        const defaultExtraMessages = [
          {
            text: this.$translations.messages.extramessages.newsletter,
            ctaLabel: this.$translations.buttons.subscribe,
            newsletter: true
          },
          {
            text: this.$translations.messages.extramessages.social,
            social: true
          },
          {
            text: this.$translations.messages.extramessages.wishlist,
          },
          {
            text: this.$translations.messages.extramessages.newcollection,
            ctaLabel: this.$translations.buttons.viewlastcollection,
            ctaLink: this.$urls.latestCollectionUrl
          },
          {
            text: this.$translations.messages.extramessages.iconicproducts,
            ctaLabel: this.$translations.buttons.viewicons,
            ctaLink: this.$urls.iconicProductsUrl
          },
          {
            text: this.$translations.messages.extramessages.packaging,
            ctaLabel: this.$translations.buttons.discovermore,
            ctaLink: this.$urls.packagingUrl
          },
          {
            text: this.$translations.messages.extramessages.paymentmethod,
            ctaLabel: this.$translations.buttons.discovermore,
            ctaLink: this.$urls.faqUrl
          }
        ];

        const shuffled = [...defaultExtraMessages].sort(() => 0.5 - Math.random());
        message.extraMessage = shuffled.slice(0, 1)[0];
      },
      startInactivityTimer() {
        const startMoment = new Date().getTime();
        const inactivityTime = new Date(startMoment + (3 * 60 * 1000)).getTime(); //inactivity time set to 3 minutes

        this.idleTimer = setInterval(() => {
          const now = new Date().getTime();
          var distance = inactivityTime - now;
          this.resetTimer = true;
          if (distance < 0) {
            clearInterval(this.idleTimer);
            this.resetTimer = false;

            this.sendFeedbackMessage();
          }
        }, 1000);
      },
      resetInactivityTimer(){
        clearInterval(this.idleTimer);
        if (this.resetTimer && !this.feedbackMessageSended) {
          this.startInactivityTimer();
        }
      },
      sendFeedbackMessage(){
        this.feedbackMessageSended = true;

        let feedbackMessage = {
          id: this.messages.length + 1,
          text: this.$translations.messages.feedback,
          feedback: true,
          entity: 'bot',
          delivered: true
        };

        this.setExtraMessage(feedbackMessage);
        this.messages.push(feedbackMessage);

        this.scrollToMessage();
      },
      async sendFeedbackToAPI(feedbackCode) {
        this.disableTextInput = true;
        let apiResponse;

        try {
          let body = {
            chatId: this.chatThreadId,
            feedback: feedbackCode
          }

          apiResponse = await this.$http
            .post(this.$urls.chatFeedbackUrl, body)
        } catch (error) {
            console.error(error);
            this.setErrorMessage(this.$translations.error.generic);
        } finally {
          if (apiResponse && apiResponse.data.status === 'ok') {
            if (this.feedbackMessageSended) {
              this.chatExpired = true;
              this.scrollToMessage();
              this.$chatbotAppElement.trigger('chatbot:stop', {chatThreadId: this.chatThreadId});
            }
          }
        }
      },
      addMultiEventListener(element, eventNames, listener) {
        var events = eventNames.split(' ');
        for (var i=0; i < events.length; i++) {
          element.addEventListener(events[i], listener, false);
        }
      },
      setErrorMessage(errorMessage) {
        this.showError = true;
        this.errorMessage = errorMessage;
      },
      hideErrorMessage(){
        this.showError = false;
        this.errorMessage = '';
      },

      // Tealium Events trigger functions
      clickedProduct(clickedProduct) {
        this.$chatbotAppElement.trigger('chatbot:onSuggestedProductClick', {chatThreadId: this.chatThreadId, item: clickedProduct});
      },
      newsletterClicked(){
        this.$chatbotAppElement.trigger('chatbot:subscribe', {chatThreadId: this.chatThreadId});
      },
      loginOpened() {
        this.$chatbotAppElement.trigger('chatbot:onLoginClick', {chatThreadId: this.chatThreadId});
      },  
    },
    computed: {
      checkCookies() {
        if (this.user.profile && Object.keys(this.user.profile).length > 0) {
          this.setCookie('chatThreadLoggedUserId', this.userId, (new Date().getTime() + (24 * 60 * 60 * 1000)));
          return this.getCookie('chatThreadIdLogged') && this.getCookie('chatThreadLoggedUserId')  && this.getCookie('chatThreadLoggedUserId') === this.user.userId;
        }

        return this.getCookie('chatThreadId') && this.getCookie('chatThreadUserId') && this.getCookie('chatThreadUserId') === this.user.userId;
      }
    },
    created() {
      this.addMultiEventListener(window, 'click mousemove keypress',() => {
        if (this.idleTimer) {
          this.resetInactivityTimer(this.idleTimer);
        }
      });

      this.$http
        .get(this.$urls.getUserInfoUrl)
        .then(response => {
          this.user.userId = response.data.userId;
          this.user.profile = response.data.userProfile ? response.data.userProfile : {};

          if(this.checkCookies) {
            this.chatThreadId = this.user.profile && Object.keys(this.user.profile).length > 0 ? this.getCookie('chatThreadIdLogged') : this.getCookie('chatThreadId');
            this.initChat();
          } else {
            this.createChatThreadId();
          }
      });
    }
}
</script>
